import * as React from "react";
import { Building2, Check, ChevronDown, Globe2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/store/authStore";
import { useMe } from "@/api/me";
import { useClientsUnscoped } from "@/api/admin";
import { cn } from "@/lib/cn";
import { useSwitchTenant } from "@/lib/impersonation";

/**
 * SuperAdmin-only "Acting as" dropdown — swaps impersonation context (and
 * therefore the branding + all scoped list queries) to a specific Client.
 *
 * Hidden entirely for non-SuperAdmin personas (the authStore.setActingAs
 * setter also silently no-ops for them, belt-and-braces).
 *
 * `compact` (mobile) collapses the trigger to an icon-only button so the
 * SuperAdmin still has a one-tap path to start impersonation on a 375px
 * viewport.  The dropdown content is identical in both modes.
 */
export function TenantSwitcher({
  compact = false,
}: {
  compact?: boolean;
} = {}): React.ReactElement | null {
  const { data: me } = useMe();
  const actingAs = useAuthStore((s) => s.actingAs);
  // R3 fix: use the unscoped hook so the dropdown always lists every
  // client the SuperAdmin can impersonate. The default `useClients` pairs
  // with the backend's acting-as-aware narrowing and would collapse to a
  // single-row list as soon as the operator picks a tenant.
  const { data: clients, isLoading } = useClientsUnscoped();
  // MV2-844: entry and exit share one invalidation path — see lib/impersonation.
  // Hooks stay above the persona gate: /me resolves after first paint, and a
  // hook that appears only once it has is "Rendered more hooks than during
  // the previous render".
  const switchTo = useSwitchTenant();

  if (me?.primary_persona !== "SuperAdmin") return null;

  const selectedLabel = actingAs?.clientName ?? "Platform (all clients)";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center rounded-md border border-[color:var(--gray-200)] bg-[color:var(--gray-0)] text-xs font-medium text-gray-700",
            "transition-colors duration-fast hover:border-[color:var(--focus)] hover:text-gray-900",
            compact ? "h-8 w-8 justify-center" : "h-8 gap-1.5 px-2.5",
            actingAs &&
              "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200",
          )}
          aria-label={
            actingAs
              ? `Switch tenant — currently acting as ${selectedLabel}`
              : "Switch tenant"
          }
          title="Acting as — pick a client tenant"
        >
          {actingAs ? <Building2 size={13} /> : <Globe2 size={13} />}
          {!compact ? (
            <>
              <span className="max-w-[180px] truncate">{selectedLabel}</span>
              <ChevronDown size={13} className="text-gray-400" />
            </>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[260px]">
        <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          Acting as
        </DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={() => switchTo(null)}
          className="flex items-center gap-2"
        >
          <Globe2 size={13} className="text-gray-400" />
          <span className="flex-1">Platform (all clients)</span>
          {!actingAs && (
            <Check size={13} className="text-[color:var(--action)]" />
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          Clients
        </DropdownMenuLabel>
        {isLoading && (
          <div className="px-2 py-1.5 text-xs text-gray-400">Loading…</div>
        )}
        {clients && clients.length === 0 && (
          <div className="px-2 py-1.5 text-xs text-gray-400">
            No clients yet
          </div>
        )}
        {clients?.map((c) => {
          const active = actingAs?.clientId === c.id;
          return (
            <DropdownMenuItem
              key={c.id}
              onSelect={() =>
                switchTo({
                  clientId: c.id,
                  clientName: c.name,
                  clientSlug: c.slug,
                })
              }
              className="flex items-center gap-2"
            >
              <Building2 size={13} className="text-gray-400" />
              <span className="flex-1 truncate">{c.name}</span>
              <span className="tabular text-[10px] text-gray-400">
                {c.site_count} sites
              </span>
              {active && (
                <Check
                  size={13}
                  className="text-[color:var(--action)]"
                />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
